import {filter, map} from 'lodash';
import {useCallback, useContext, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Button, CopyText, IconName, IconColor, Icon, ParticipantInfo} from 'components';
import {SystemEndSessionCode} from 'constants/session';
import {UserSessionContext} from 'context';
import {useUserSession} from 'hooks';
import {SessionResultStatus} from 'interfaces';
import {UnauthorizedLayout} from 'layouts';
import {TRANSLATION_KEYS} from 'locale';
import {getDuration, sessionUtils} from 'utils';

const T_KEYS = TRANSLATION_KEYS.IN_SESSION_PAGE.ENDED_VIEW;
const SESSION_T_KEYS = TRANSLATION_KEYS.COMMON.SESSION;

type Props = {
    onReturnToSession?: () => void;
}

export const SessionEndedView: React.FC<Props> = ({onReturnToSession}) => {
    const {t} = useTranslation();
    const {feedbackSent, openDefaultPage, toggleFeedbackModal, localSessionEnded, deescalated} = useContext(UserSessionContext);

    const {session, canSendFeedback, isEndedBySystem, sessionResultInformation, reassigned, canManageSession, isEnded} = useUserSession();

    const resultInfo: {label: string, iconName: IconName, iconColor: IconColor} = useMemo(() => {
        const status = session.result?.status;
        const information = session.result?.information;

        if (status === SessionResultStatus.Success) {
            return {
                label: information || t(T_KEYS.SUCCESSFUL),
                iconName: 'check-circle',
                iconColor: 'success'
            };
        } else if (status === SessionResultStatus.Failed) {
            return {
                label: information || t(T_KEYS.FAILED),
                iconName: 'x-circle',
                iconColor: 'alert'
            };
        }

    }, [t, session]);

    const duration = useMemo(() => getDuration(session.inSessionStartDate || session.startDate, session.endDate || new Date()), [session]);

    const isUserParticipantLeft = useMemo(() =>
        deescalated || (!canManageSession && localSessionEnded && !isEnded),
    [deescalated, canManageSession, localSessionEnded, isEnded]);

    const sessionEndedLabel = useMemo(() => {
        if (isUserParticipantLeft) {
            return t(T_KEYS.LEFT);
        }
        if (isEndedBySystem && !reassigned) {
            return t(T_KEYS.FINISHED_BY_SYSTEM);
        }
        return t(T_KEYS.FINISHED);
    }, [isUserParticipantLeft, reassigned, isEndedBySystem, t]);

    const sessionEndedDetails = useMemo(() => isEndedBySystem && sessionResultInformation === SystemEndSessionCode.MaxDurationExceeded
        ? t(T_KEYS.DETAILS.MAX_DURATION_EXCEEDED) : null, [isEndedBySystem, sessionResultInformation, t]);

    const visibleParticipants = useMemo(() =>
        sessionUtils.sortParticipantList(filter(session.participants, (participant) => !participant.removed)), [session]);

    const returnToSession = useCallback(() => {
        if (!session && !isUserParticipantLeft) {
            return;
        }
        onReturnToSession();
    }, [session, isUserParticipantLeft, onReturnToSession]);

    return (
        <UnauthorizedLayout>
            <div className='tw-flex-center tw-flex-col tw-text-default-dimmed tw-mt-10'>
                <div className='tw-ts-h2 tw-mb-2 tw-text-center'>
                    {`${sessionEndedLabel} (${duration })`}
                </div>
                {sessionEndedDetails &&
                    <div className='tw-ts-h4 tw-mb-2 tw-text-center'>
                        {sessionEndedDetails}
                    </div>}
                <div className='tw-flex-center tw-mb-10'>
                    <div className='tw-ts-h5'>
                        <div className='tw-flex tw-items-center'>
                            <Icon name='id' size='lg' color='default-dimmed' className='tw-mr-1' />
                            <span>{session.id}</span>
                        </div>
                    </div>
                    <CopyText text={session.id} iconOnly textInTooltip={false} className='tw-ml-1' />
                </div>
                {resultInfo &&
                    <div className='tw-flex-center tw-flex-col'>
                        <Icon name={resultInfo.iconName} color={resultInfo.iconColor} size='8xl' thickness='sm' />
                        <div className='tw-ts-h2 tw-mt-4 tw-max-w-xl tw-text-center tw-text-break-word'>
                            {resultInfo.label}
                        </div>
                    </div>}
                {isUserParticipantLeft && !deescalated &&
                    <Button
                        width='small'
                        rounded
                        className='tw-px-4'
                        testAttribute={{id: 'go-to-main-page-button'}}
                        icon='chevron-left'
                        onClick={returnToSession}
                    >
                        {t(T_KEYS.RETURN_TO_SESSION)}
                    </Button>}
                <Button
                    color='primary'
                    width='large'
                    rounded
                    className='tw-text-lg tw-mt-10'
                    testAttribute={{id: 'go-to-main-page-button'}}
                    onClick={openDefaultPage}
                >
                    {t(T_KEYS.MAIN_PAGE_BTN)}
                </Button>
                {canSendFeedback &&
                    <div
                        className='tw-flex tw-items-center tw-ts-h5 tw-text-default-dimmed tw-mt-4 tw-cursor-pointer'
                        onClick={toggleFeedbackModal}
                    >
                        {t(feedbackSent ? SESSION_T_KEYS.FEEDBACK.EDIT_MY : SESSION_T_KEYS.FEEDBACK.ADD_MY)}
                        <Icon name='arrow-right' color='default-dimmed' thickness='md' className='tw-ml-1' />
                    </div>}
                {/* TODO: Create a component and reuse here and on join page */}
                <div className='tw-py-2 tw-px-4 tw-border tw-border-default tw-mt-4 tw-w-full'>
                    <div className='tw-ts-h5 tw-text-default-dimmed tw-ml-7'>
                        {t(SESSION_T_KEYS.LABELS.PARTICIPANTS)}
                    </div>
                    <ParticipantInfo
                        session={session}
                        participant={session.localParticipant}
                        local
                        fields={{icon: true}}
                        nameStyle='h3-bold-dimmed'
                    />
                    {map(visibleParticipants, (participant) => (
                        <ParticipantInfo
                            key={participant.id}
                            session={session}
                            participant={participant}
                            fields={{icon: true, createClientConnection: canManageSession}}
                            nameStyle='h3-bold-dimmed'
                        />
                    ))}
                </div>
            </div>
        </UnauthorizedLayout>
    );
};